import { useState } from 'react';
import axiosInstance, { ensureCsrfToken } from '../utils/axiosInstance';

/**
 * Custom hook for handling form state and API operations
 * @param {Object} initialForm - Initial form state
 * @returns {Object} Form state and handlers
 * @property {Object} form - Current form state
 * @property {Function} setForm - Form state setter
 * @property {Function} post - POST request handler
 * @property {Function} put - PUT request handler
 * @property {Function} patch - PATCH request handler
 * @property {boolean} loading - Loading state
 * @property {Object} errors - Form validation errors
 * @property {Function} setErrors - Errors state setter
 * @property {Function} handleOnChange - Input change handler
 */
export const useForm = (initialForm) => {
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [form, setForm] = useState(initialForm);

    /**
     * Handles form input changes
     * @param {Object} e - Event object or synthetic event object
     * @param {Object} e.target - Event target
     * @param {string} e.target.name - Field name
     * @param {*} e.target.value - Field value
     */
    const handleOnChange = (e) => {
        const { name, value } = e.target;

        // Handle nested object paths (e.g., 'panel.manufacturer')
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setForm((prev) => {
                const parentObj = prev[parent];
                let transformedValue = value;
                if (typeof initialForm[parent][child] === 'number') {
                    transformedValue = Number(value);
                } else if (e.target.type === 'radio') {
                    transformedValue = value;
                }
                return {
                    ...prev,
                    [parent]: {
                        ...parentObj,
                        [child]: transformedValue
                    }
                };
            });
        } else {
            let transformedValue = value;
            if (typeof initialForm[name] === 'number') {
                transformedValue = Number(value);
            } else if (e.target.type === 'radio') {
                transformedValue = value;
            }
            setForm((prev) => ({
                ...prev,
                [name]: transformedValue
            }));
        }

        // Clear any errors for this field
        if (errors) {
            const errorKey = name;
            if (errors[errorKey]) {
                setErrors((prev) => {
                    const temp = { ...prev };
                    delete temp[errorKey];
                    return temp;
                });
            }
        }
    };

    /**
     * Handles POST requests with login-specific behavior
     * @param {string} url - API endpoint
     * @returns {Promise} API response
     */
    const login = async (url) => {
        setLoading(true)
        setErrors({})
        try {
            // Ensure CSRF token is set
            await ensureCsrfToken();
            
            // Then make the login request
            const response = await axiosInstance.post(url, form);

            return response.data;
        } catch (err) {
            console.log('errors on login', err);
            if (err.response?.status === 422) {
                const errorsObj = err.response?.data?.errors;
                setErrors(errorsObj);
            }
            throw err;
        } finally {
            setLoading(false);
        }
    }

    /**
     * Handles POST requests
     * @param {string} url - API endpoint
     * @returns {Promise} API response
     */
    const post = async (url) => {
        setLoading(true);
        setErrors({});
        return new Promise((resolve, reject) => {
            axiosInstance
                .post(url, form)
                .then((response) => {
                    resolve(response.data);
                })
                .catch((err) => {
                    console.log('errors on post', err);
                    if (err.response?.status === 422) {
                        const errorsObj = err.response?.data?.errors;
                        setErrors(errorsObj);
                    }

                    reject(err);

                })
                .finally(() => {
                    setLoading(false);
                });
        });
    };

    /**
     * Handles PUT requests
     * @param {string} url - API endpoint
     * @returns {Promise} API response
     */
    const put = async (url) => {
        setLoading(true);
        setErrors({});
        return new Promise((resolve, reject) => {
            axiosInstance
                .put(url, form)
                .then((response) => {
                    resolve(response.data);
                })
                .catch((err) => {
                    console.log('errors on put', err);
                    const errorsObj = err.response?.data?.errors;
                    setErrors(errorsObj);
                    reject(err);
                })
                .finally(() => {
                    setLoading(false);
                });
        });
    };

    /**
     * Handles PATCH requests
     * @param {string} url - API endpoint
     * @returns {Promise} API response
     */
    const patch = async (url) => {
        setLoading(true);
        setErrors({});
        return new Promise((resolve, reject) => {
            axiosInstance
                .patch(url, form)
                .then((response) => {
                    resolve(response.data);
                })
                .catch((err) => {
                    console.log('errors on patch', err);
                    const errorsObj = err.response?.data?.errors;
                    setErrors(errorsObj);
                    reject(err);
                })
                .finally(() => {
                    setLoading(false);
                });
        });
    };

    return {
        form,
        setForm,
        post,
        put,
        patch,
        loading,
        errors,
        setErrors,
        handleOnChange,
        login
    };
};
